import React, { useState, useRef } from "react";
import {
  HiX,
  HiOutlineBriefcase,
  HiOutlineClock,
  HiOutlineVideoCamera,
  HiOutlinePhotograph,
} from "react-icons/hi";

// --- HELPERS DEFINED BEFORE THE MAIN COMPONENT ---
const DAYS_OF_WEEK_LIST = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const SHIFTS_LIST = [
  { id: "morning", label: "Morning", start: "08:00", end: "12:00" },
  { id: "afternoon", label: "Afternoon", start: "13:00", end: "17:00" },
  { id: "evening", label: "Evening", start: "18:00", end: "22:00" },
];

const initializeWorkSchedule = () => {
  const schedule = {};
  DAYS_OF_WEEK_LIST.forEach((day) => {
    schedule[day] = {
      enabled: false,
      shifts: SHIFTS_LIST.map((s) => ({ ...s, enabled: false })),
    };
  });
  return schedule;
};

const SectionHeader = ({ icon, title, color = "text-gray-900" }) => (
  <div className={`flex items-center gap-2 ${color} text-left`}>
    {icon}
    <span className="text-[11px] font-black uppercase tracking-[0.2em]">
      {title}
    </span>
  </div>
);

const InputField = ({ label, type = "text", ...props }) => (
  <div className="flex flex-col items-start space-y-2 text-left">
    <label className="text-[11px] font-bold text-gray-500 uppercase ml-1 tracking-wider">
      {label}
    </label>
    <input
      type={type}
      className="w-full px-5 py-3.5 rounded-2xl border-2 border-gray-100 bg-gray-50/30 focus:bg-white focus:border-blue-500 outline-none transition-all text-sm font-medium"
      {...props}
    />
  </div>
);

const DayRow = ({ dayId, data, onToggle, onShiftToggle }) => (
  <div
    className={`p-4 rounded-2xl border transition-all flex flex-col md:flex-row md:items-center justify-between gap-4 ${
      data.enabled
        ? "border-blue-200 bg-blue-50/20"
        : "border-gray-100 bg-gray-50/30 opacity-60"
    }`}
  >
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        checked={data.enabled}
        onChange={(e) => onToggle(e.target.checked)}
        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <span className="font-bold text-gray-800 capitalize">{dayId}</span>
    </div>
    {data.enabled && (
      <div className="flex flex-wrap gap-2">
        {data.shifts.map((shift, idx) => (
          <button
            key={shift.id}
            type="button"
            onClick={() => {
              const newShifts = [...data.shifts];
              newShifts[idx].enabled = !newShifts[idx].enabled;
              onShiftToggle(newShifts);
            }}
            className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-tighter border transition-all ${
              shift.enabled
                ? "bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-100"
                : "bg-white border-gray-200 text-gray-400"
            }`}
          >
            {shift.label}
          </button>
        ))}
      </div>
    )}
  </div>
);

// --- MAIN COMPONENT ---
// Video Consulting only: API may require a default hospital_id for DB constraint
const VIDEO_CONSULTING_HOSPITAL_ID = 5;

const DoctorApplyModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    bio: "",
    job_experience: "0",
    profession: "",
    languages: "",
    type: "Video Consulting",
    hospital_id: String(VIDEO_CONSULTING_HOSPITAL_ID),
    image: null,
    id_card: null,
    certificate: null,
    availability_status: "Available",
    work_schedule: initializeWorkSchedule(),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (field) => (e) => {
    const file = e.target.files?.[0];
    if (file) setFormData((prev) => ({ ...prev, [field]: file }));
  };

  const setScheduleValue = (dayId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      work_schedule: {
        ...prev.work_schedule,
        [dayId]: { ...prev.work_schedule[dayId], [field]: value },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "work_schedule")
          data.append(key, JSON.stringify(formData[key]));
        else if (key === "image" && formData.image)
          data.append("image", formData.image);
        else if (key === "id_card" && formData.id_card)
          data.append("id_card", formData.id_card);
        else if (key === "certificate" && formData.certificate)
          data.append("certificate", formData.certificate);
        else if (key === "hospital_id")
          data.append("hospital_id", formData.hospital_id || VIDEO_CONSULTING_HOSPITAL_ID);
        else if (key !== "image" && key !== "id_card" && key !== "certificate" && formData[key] != null && formData[key] !== "")
          data.append(key, formData[key]);
      });

      const response = await fetch("https://app.caaficare.so/api/doctor", {
        method: "POST",
        body: data,
      });
      const result = await response.json();
      if (result.success) {
        alert("Application Sent!");
        onClose();
      }
    } catch (error) {
      alert("Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
        <div className="px-10 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="text-left">
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">
              Doctor Onboarding
            </h2>
            <p className="text-sm text-gray-500 font-medium">
              Apply as a Video Consulting doctor on CaafiCare.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-50 hover:text-red-500 rounded-full transition-all text-gray-400"
          >
            <HiX size={24} />
          </button>
        </div>

        <form
          ref={scrollRef}
          onSubmit={handleSubmit}
          className="overflow-y-auto p-10 space-y-10"
        >
          <SectionHeader
            icon={<HiOutlineBriefcase />}
            title="Professional Identity"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Full Name *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <InputField
              label="Phone Number *"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField
              label="Specialty"
              name="profession"
              value={formData.profession}
              onChange={handleChange}
            />
            <InputField
              label="Experience"
              name="job_experience"
              value={formData.job_experience}
              onChange={handleChange}
            />
            <InputField
              label="Languages"
              name="languages"
              value={formData.languages}
              onChange={handleChange}
            />
          </div>

          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border-2 border-blue-200 bg-blue-50/50">
            <HiOutlineVideoCamera className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">
                Application type
              </p>
              <p className="text-sm font-bold text-gray-900">
                Video Consulting
              </p>
            </div>
          </div>

          <SectionHeader
            icon={<HiOutlinePhotograph />}
            title="Documents (all optional)"
            color="text-blue-600"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-2 text-left">
              <label className="text-[11px] font-bold text-gray-500 uppercase ml-1 tracking-wider">
                Profile Image *
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange("image")}
                className="w-full px-4 py-3 rounded-2xl border-2 border-gray-100 bg-gray-50/30 focus:bg-white focus:border-blue-500 outline-none transition-all text-sm file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-blue-50 file:text-blue-600 file:font-bold file:text-sm"
              />
              {formData.image && (
                <p className="text-xs text-green-600 font-medium truncate max-w-full">
                  {formData.image.name}
                </p>
              )}
            </div>
            <div className="flex flex-col items-start space-y-2 text-left">
              <label className="text-[11px] font-bold text-gray-500 uppercase ml-1 tracking-wider">
                ID Card *
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange("id_card")}
                className="w-full px-4 py-3 rounded-2xl border-2 border-gray-100 bg-gray-50/30 focus:bg-white focus:border-blue-500 outline-none transition-all text-sm file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-blue-50 file:text-blue-600 file:font-bold file:text-sm"
              />
              {formData.id_card && (
                <p className="text-xs text-green-600 font-medium truncate max-w-full">
                  {formData.id_card.name}
                </p>
              )}
            </div>
            <div className="flex flex-col items-start space-y-2 text-left">
              <label className="text-[11px] font-bold text-gray-500 uppercase ml-1 tracking-wider">
                Certificate *
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange("certificate")}
                className="w-full px-4 py-3 rounded-2xl border-2 border-gray-100 bg-gray-50/30 focus:bg-white focus:border-blue-500 outline-none transition-all text-sm file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-blue-50 file:text-blue-600 file:font-bold file:text-sm"
              />
              {formData.certificate && (
                <p className="text-xs text-green-600 font-medium truncate max-w-full">
                  {formData.certificate.name}
                </p>
              )}
            </div>
          </div>

          <SectionHeader
            icon={<HiOutlineClock />}
            title="Availability"
            color="text-orange-500"
          />
          <div className="grid grid-cols-1 gap-3 text-left">
            {Object.keys(formData.work_schedule).map((dayId) => (
              <DayRow
                key={dayId}
                dayId={dayId}
                data={formData.work_schedule[dayId]}
                onToggle={(val) => setScheduleValue(dayId, "enabled", val)}
                onShiftToggle={(newShifts) =>
                  setScheduleValue(dayId, "shifts", newShifts)
                }
              />
            ))}
          </div>

          <div className="flex gap-4 pt-10 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 font-bold text-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-[2] py-4 bg-blue-600 text-white rounded-2xl font-black"
            >
              {loading ? "Processing..." : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DoctorApplyModal;
